import { getStationMarkerProps } from '../helpers/map-marker';
import { MAX_SERVICE_LOGO_MARKER_ZOOM } from '../config';

import { addMapMarkerToMapboxMap } from '@/lib/helpers/mapbox';
import { objectToStringSorted } from '@/lib/utils/objects';

import type { Feature, FeatureCollection } from 'geojson';
import type {
  Map,
  CircleLayer,
  GeoJSONSource,
  GeoJSONSourceRaw,
  SymbolLayer,
  FitBoundsOptions,
} from 'mapbox-gl';

export interface UseStationLayers {
  /**
   * The ID of the symbol layer for the stations
   */
  symbolStationsLayerId: string;
  /**
   * The map configuration for the stations
   */
  stationsMapConfig: ClientState['stationsMapConfig'];
  /**
   * Fit the map to the bounds of the stations
   */
  fitBoundStations: () => Promise<void>;
  /**
   * Whether the stations are ready to display
   */
  isReady: Ref<boolean>;
}

export const symbolStationsLayerId = 'symbol-stations-layer';
export const stationsClusterLayerId = 'stations-cluster-layer';
export const clusterCountLayerId = 'cluster-count-stations-layer';
export const stationsClusterHaloLayerId = 'stations-cluster-halo-layer';

export function useStationLayers(
  map: Ref<Map | undefined>,
  stations: Ref<MapStation[]>,
  selectedStationId: Ref<string | null>,
  options?: {
    fitBounds?: (
      coordinates: Coordinates[],
      options?: FitBoundsOptions
    ) => Promise<void>;
  }
): UseStationLayers {
  const { theme, stationsMapConfig } = useClientStore().clientState;

  const stationFeatures = ref<Feature[]>([]);
  const stationsSourceId = 'stations-source';

  const isReady = ref(false);

  async function updateStationFeatures(): Promise<void> {
    stationFeatures.value = await Promise.all(
      stations.value.map(async station => {
        const isSelected = station.id === selectedStationId.value;
        const markerProps = getStationMarkerProps(station, isSelected, theme);
        await addMapMarkerToMapboxMap(map, markerProps);

        const image = objectToStringSorted(markerProps);

        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: station.coordinates,
          },
          properties: {
            id: station.id,
            image,
            entityType: 'station',
            isClosed: station.isClosed,
            availableBikes: station.availableBikes,
          },
        } satisfies Feature;
      })
    );
  }

  // Arbitrary cluster radius which works well with the interpolation
  const clusterRadius = 80;

  function syncStationsSource(): void {
    if (!map.value) return;

    const source: GeoJSONSourceRaw = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: stationFeatures.value,
      },
      cluster: true,
      clusterMaxZoom: 13,
      clusterRadius,
      clusterMinPoints: 0,
      clusterProperties: {
        sum: ['+', ['get', 'availableBikes']],
      },
    };

    if (!map.value.getSource(stationsSourceId)) {
      map.value.addSource(stationsSourceId, source);
    } else {
      (map.value.getSource(stationsSourceId) as GeoJSONSource)?.setData(
        source.data as FeatureCollection
      );
    }
  }

  function registerLayers(): void {
    if (!map.value) return;

    const stationsClusterHaloLayer: CircleLayer = {
      id: stationsClusterHaloLayerId,
      type: 'circle',
      source: stationsSourceId,
      filter: ['has', 'sum'],
      paint: {
        'circle-radius': [
          'interpolate',
          ['exponential', 2],
          ['zoom'],
          0,
          30,
          14,
          clusterRadius,
          22,
          clusterRadius * 2 ** 8,
        ],
        'circle-color': theme.colorPrimary,
        'circle-opacity': 0.2,
      },
      minzoom: MAX_SERVICE_LOGO_MARKER_ZOOM,
    };
    if (!map.value.getLayer(stationsClusterHaloLayerId)) {
      map.value.addLayer(stationsClusterHaloLayer);
    }

    const stationsClusterLayer: CircleLayer = {
      id: stationsClusterLayerId,
      type: 'circle',
      source: stationsSourceId,
      filter: ['has', 'sum'],
      paint: {
        'circle-color': theme.colorPrimary,
        'circle-radius': 24,
      },
      minzoom: MAX_SERVICE_LOGO_MARKER_ZOOM,
    };
    if (!map.value.getLayer(stationsClusterLayerId)) {
      map.value.addLayer(stationsClusterLayer);
    }

    const clusterCountLayer: SymbolLayer = {
      id: clusterCountLayerId,
      type: 'symbol',
      source: stationsSourceId,
      filter: ['has', 'sum'],
      layout: {
        'text-field': ['get', 'sum'],
        'text-font': ['Roboto Black'],
        'text-size': 18,
        'text-offset': [0, 0.1],
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': theme.colorNeutralLight5,
        'text-opacity-transition': {
          duration: 0,
          delay: 0,
        },
      },
      minzoom: MAX_SERVICE_LOGO_MARKER_ZOOM,
    };
    if (!map.value.getLayer(clusterCountLayerId)) {
      map.value.addLayer(clusterCountLayer);
    }

    const symbolStationsLayer: SymbolLayer = {
      id: symbolStationsLayerId,
      type: 'symbol',
      source: stationsSourceId,
      layout: {
        'icon-image': ['get', 'image'],
        'icon-size': [
          'interpolate',
          ['exponential', 2],
          ['zoom'],
          0,
          0.4,
          13,
          0.5,
        ],
        'icon-ignore-placement': true,
        'icon-allow-overlap': true,
        'icon-anchor': 'bottom',
      },
      minzoom: MAX_SERVICE_LOGO_MARKER_ZOOM,
    };
    if (!map.value.getLayer(symbolStationsLayerId)) {
      map.value.addLayer(symbolStationsLayer);
    }
  }

  async function fitBoundStations(): Promise<void> {
    const coordinates = stations.value
      .map(station => station.coordinates ?? null)
      .filter((stationCoords): stationCoords is Coordinates =>
        Boolean(stationCoords)
      );
    if (!coordinates.length) return;

    await options?.fitBounds?.(coordinates, {
      padding: stationsMapConfig?.fitBoundsPadding,
      animate: false,
    });
  }

  watchImmediate([stations, selectedStationId], async () => {
    await until(map).not.toBeUndefined();

    await updateStationFeatures();
    await syncStationsSource();

    registerLayers();

    if (
      stations.value.length &&
      stationsMapConfig?.initialFitBounds &&
      !isReady.value
    ) {
      await fitBoundStations();
      isReady.value = true;
    }
  });

  return {
    symbolStationsLayerId,
    stationsMapConfig,
    fitBoundStations,
    isReady,
  };
}
